import { z } from 'zod';

import { isValidEmail } from 'components/LegacyInput/utils';

import { SIXTY_DAYS_LIMIT } from '../constants';
import { getFortyFiveDaysEndOfMonth } from '../utils';
import {
  generalContentFormSchema,
  hiddenGeneralContentFormSchema,
} from './generalContentForm.schema';
import locales from './locales';
import {
  displayTradeNameBooleanFormSchema,
  getLateFeePercentageFormSchema,
  invoiceSettingsIdFormSchema,
  issuerEmailNullableFormSchema,
  lateMaxDaysFormSchema,
  legalMentionsArrayFormSchema,
} from './optionForm.schema';
import { paymentFormSchema } from './paymentForm.schema';
import { hiddenTasksFormSchema, tasksFormSchema } from './tasksForm.schema';

interface GetInvoiceFormSchemaArgs {
  minLatePaymentFee: number;
}

export const getInvoiceFormSchema = ({
  minLatePaymentFee,
}: GetInvoiceFormSchemaArgs) =>
  z
    .object({
      ...generalContentFormSchema.shape,
      ...hiddenGeneralContentFormSchema.shape,
      ...paymentFormSchema.shape,
      lateFeePercentage: getLateFeePercentageFormSchema(minLatePaymentFee),
      lateMaxDays: lateMaxDaysFormSchema,
      publicPaymentId: z.string(),
      settings: z.object({
        displayTradeName: displayTradeNameBooleanFormSchema,
        invoiceSettingsId: invoiceSettingsIdFormSchema,
        issuerEmail: issuerEmailNullableFormSchema,
        legalMentions: legalMentionsArrayFormSchema,
      }),
      tasks: z.array(z.intersection(hiddenTasksFormSchema, tasksFormSchema)),
    })

    /**
     * Not using `z.array().nonempty()` because the output type would require at least one task.
     * Since the baseInvoice can be empty, it would cause TypeScript errors
     */
    .refine(({ tasks }) => tasks.length > 0, {
      message: locales.errors.missingTasks,
      path: ['tasks'],
    })

    .refine(
      ({ allowCreditCardPayment, allowIbanPayment }) =>
        allowCreditCardPayment || allowIbanPayment,
      { message: locales.errors.missingPaymentMethod },
    )

    .refine(
      ({ settings: { issuerEmail } }) =>
        issuerEmail ? isValidEmail(issuerEmail) : true,
      {
        message: locales.errors.invalidIssuerEmail,
        path: ['settings', 'issuerEmail'],
      },
    )

    .refine(
      ({ lateMaxDays, startAt }) => {
        const fortyFiveDaysEndOfMonth = getFortyFiveDaysEndOfMonth(startAt);
        return (
          lateMaxDays <= Math.max(fortyFiveDaysEndOfMonth, SIXTY_DAYS_LIMIT)
        );
      },
      { message: locales.errors.delayTooHigh, path: ['lateMaxDays'] },
    );

export type InvoiceFormSchema = z.infer<
  ReturnType<typeof getInvoiceFormSchema>
>;
